/**
 * ElevenLabs client tools: the agent decides to call them, the browser runs
 * them (fetch to our Bun server + mocked data) and returns the result to the
 * LLM. The return value is a JSON string the model rephrases.
 */

import { fullName, loadProfile } from "./user";
import { getLocale, strings } from "./ui.strings";

const lang = getLocale();

/** Account identity, read at call time (localStorage profile). */
const accountName = () => {
  const profile = loadProfile();
  return profile ? fullName({ profile }) : "Invité";
};
const accountPhone = () => loadProfile()?.phone ?? "";

type ToolNotifier = (params: { label: string }) => void;

// Fallback: place de la République when geolocation is denied
const FALLBACK_POSITION = { lat: 48.8674, lng: 2.363 };

const getPosition = () =>
  new Promise<{ lat: number; lng: number }>((resolve) => {
    if (!navigator.geolocation) return resolve(FALLBACK_POSITION);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      () => resolve(FALLBACK_POSITION),
      { timeout: 4000, maximumAge: 60000 },
    );
  });

const fetchJson = async ({ path }: { path: string }) => {
  const response = await fetch(path);
  if (!response.ok) return JSON.stringify({ error: `HTTP ${response.status}` });
  return JSON.stringify(await response.json());
};

const postJson = async ({ path, body }: { path: string; body: unknown }) => {
  const accessCode = loadProfile()?.accessCode;
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessCode ? { "x-access-code": accessCode } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return JSON.stringify({ error: `HTTP ${response.status}` });
  return JSON.stringify(await response.json());
};

export const createClientTools = ({ notify }: { notify: ToolNotifier }) => ({
  get_pharmacy_status: async () => {
    notify({ label: strings.tool_hours });
    return fetchJson({ path: `/api/pharmacy/status?lang=${lang}` });
  },

  find_nearby_pharmacies: async () => {
    notify({ label: strings.tool_nearby });
    const { lat, lng } = await getPosition();
    return fetchJson({ path: `/api/pharmacies/nearby?lat=${lat}&lng=${lng}&lang=${lang}` });
  },

  get_service_info: async ({ query }: { query: string }) => {
    notify({ label: `${strings.tool_service} « ${query} »` });
    return fetchJson({ path: `/api/services?q=${encodeURIComponent(query)}&lang=${lang}` });
  },

  check_order_status: async ({ reference }: { reference?: string } = {}) => {
    const query = reference?.trim() || accountName();
    notify({ label: `${strings.tool_orders}${reference ? ` (${reference})` : ""}` });
    return fetchJson({ path: `/api/orders/search?q=${encodeURIComponent(query)}` });
  },

  book_appointment: async ({ service, day, time }: { service: string; day: string; time: string }) => {
    notify({ label: `${strings.tool_appointment} ${service} — ${day} ${time}` });
    return postJson({
      path: "/api/appointments",
      body: { customerName: accountName(), service, day, time },
    });
  },

  cancel_order: async ({ reference }: { reference: string }) => {
    notify({ label: `${strings.tool_cancel} ${reference}` });
    return postJson({
      path: "/api/orders/cancel",
      body: { reference, customerName: accountName() },
    });
  },

  send_whatsapp_search_link: async ({ productName }: { productName?: string } = {}) => {
    notify({ label: strings.tool_whatsapp });
    return postJson({
      path: "/api/handoff/whatsapp",
      body: { phone: accountPhone(), productName, lang },
    });
  },

  get_product_advice: async ({ product, topic }: { product: string; topic?: string }) => {
    notify({ label: `${strings.tool_advice} « ${product} »` });
    const topicParam = topic ? `&topic=${encodeURIComponent(topic)}` : "";
    return fetchJson({ path: `/api/advice?product=${encodeURIComponent(product)}${topicParam}` });
  },

  leave_message: async ({ message }: { message: string }) => {
    notify({ label: strings.tool_message });
    return postJson({
      path: "/api/messages",
      body: { customerName: accountName(), message },
    });
  },
});
